import { MODEL_CHANGING_EVENTS, DIAGRAM_EMIT_EVENTS } from "../diagram/modules/DiagramEvents";
import { DiagramEventEmitter } from "../diagram/modules/DiagramEventEmitter";

/**
 * @description - FabricDiagramStateHolder is used for the history functionality.
 * It does not responde to keyboard events as the purpouse of this functionality was to merge with the ui that it uses.
 * There are a number of api's that provide access to some of the information inside  this class.
 * This is a singleton per diagram instance.
 * It recieves a diagramInstanceUuid and if it already has a instance created for that specific instance, it returns that one otherwise it creates a new one.
 */
export const FabricDiagramStateHolder = (function () {
    const instance = {};
    class FabricDiagramStateHolder {
        // max length of the undo cache
        stateCacheMaxLength;

        // the diagram object model for each last events
        state = [];

        // the diagram object model for each last events
        redo = [];

        // the view instance
        viewInstance;

        // last event that happened
        // lastEvent = null;
        // the instance uuid of the fabric diagram
        instanceUuid;

        // event emitter instance
        eventEmitter;

        // boolean - true if Data Model Instance
        isDataModel;

        constructor(diagramInstanceUuid) {
            if (!instance[diagramInstanceUuid]) {
                // setting the singleton instance
                instance[diagramInstanceUuid] = this;
                this.instanceUuid = diagramInstanceUuid;
            }
            return instance[diagramInstanceUuid];
        }

        /**
         * @description - Initializez the class config.
         * @param {Object} config - Object {stateCacheMaxLength}. The property stateCacheMaxLength is used to define the maximum history cache length. Default is 20
         */
        init(config) {
            if (!config) config = {};
            this.stateCacheMaxLength = config.stateCacheMaxLength || 20;
            this.eventEmitter = new DiagramEventEmitter(this.instanceUuid);
        }

        /**
         * @description sets the grid view instance
         * @param {GridView} viewInstance -
         * @returns { void }
         */
        setViewInstance(viewInstance) {
            this.viewInstance = viewInstance;
        }

        /**
         * @description Recieves a history valid event emitted by the diagram from FabricDiagram.
         * History valid meaning the events are filtered beefore being sent to this instance to only match certain events
         * @param {String} event.type - The name of the event
         * @param {Object} event.payload - The data of the payload.
         */
        eventHappened(event) {
            if (!this.isModelChangingEvent(event)) {
                return;
            }
            // if (this.lastEvent === event.type && this.state.length) {
            //     this.state[this.state.length - 1].newState = this.viewInstance.getDiagramData();
            //     this.lastEvent = event.type;
            //     return;
            // }
            // this.lastEvent = event.type;
            const state = event.type === DIAGRAM_EMIT_EVENTS.VIEW_MODEL_LOADED ? event.payload.data : this.getCurrentState();
            this.addNewState(event, state);
            this.clearRedo();
        }

        /**
         * @description If the state is not full, it pushes the change, otherwise it removes the oldest element in the array (index 0 ) and then pushes the new change
         * @param {String} event  - the name of the evvent
         * @param {Object} state  - the diagram object model before the change occured
         * @returns { void }
         */
        addNewState(event, state) {
            if (this.state.length === 20) {
                this.state.splice(0, 1);
            }
            this.state.push({ event, state });
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.DIAGRAM_STATE_MODIFICATIONS, this.state);
        }

        /**
         * @description - Returns the length of the undo cache
         */
        getStateCacheLength() {
            return this.state.length;
        }

        /**
         * @description - Returns the length of the redo cache
         */
        getRedoCacheLength() {
            return this.redo.length;
        }

        /**
         * @description Returns the current state of the grid view
         * @returns { Object } - The diagram object model
         */
        getCurrentState() {
            return !this.viewInstance ? {} : this.viewInstance.getDiagramData();
        }

        /**
         * @description clears the undo state
         */
        clearRedoCache() {
            this.state = [];
        }

        /**
         * @description clears the redo state
        */
        clearUndoCache() {
            this.redo = [];
        }

        performRedoState(emitEvents) {
            if (this.redo.length === 0) return;
            const lastRedoIndex = this.redo.length - 1;
            const lastRedoObj = { ...this.redo[lastRedoIndex] };
            let newState = Array.isArray(lastRedoObj.event.payload) ? lastRedoObj.event.payload[0].newState : (lastRedoObj.event.payload.newState || lastRedoObj.state);
            if (!newState && lastRedoObj.event.payload[0]) {
                newState = lastRedoObj.event.payload[0].payload.newState;
            }
            this.state.push(lastRedoObj);
            this.redo.splice(lastRedoIndex, 1);
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.DIAGRAM_STATE_MODIFICATIONS, this.state);
            this.viewInstance.changeDataSet(newState || lastRedoObj.newState || lastRedoObj.state, true, this.isDataModel);
            if (emitEvents) {
                this.emitEventsForAllChanges(lastRedoObj, "REDO");
            }
        }

        performUndoState(emitEvents) {
            if (this.state.length === 0) return;
            const lastChange = this.state[this.state.length - 1];
            this.redo.push({ ...lastChange });
            this.state.splice(this.state.length - 1, 1);
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.DIAGRAM_STATE_MODIFICATIONS, this.state);
            let oldState = Array.isArray(lastChange.event.payload) ? lastChange.event.payload[0].oldState : lastChange.event.payload.oldState;
            if (!oldState && lastChange.event.payload[0]) {
                oldState = lastChange.event.payload[0].payload.oldState;
            }
            this.viewInstance.changeDataSet(oldState || lastChange.state, true, this.isDataModel);
            if (emitEvents) {
                this.emitEventsForAllChanges(lastChange, "UNDO");
            }
        }

        clearRedo() {
            this.redo = [];
        }

        /**
         * @description Clears component state and refernece
         * @param {String} instanceUuid  - Destroys class instance for provided canvas instance uuid
         */
        destroy(instanceUuid) {
            this.state = [];
            this.viewInstance = null;
            this.redo = [];
            this.stateCacheMaxLength = null;
            instance[instanceUuid] = null;
            delete instance[instanceUuid];
        }

        /**
         * @description Checks to see if is a model changing event based on a string const array
         * @param { Object } event { payload, type} type indicates event name, payload holds the event data
         */
        isModelChangingEvent(event) {
            return MODEL_CHANGING_EVENTS.indexOf(event.type) !== -1;
        }

        /**
         * @description Emits events for changes
         * @param {Object} - {type, payload} Last event
         */
        emitEventsForAllChanges(lastChange, from) {
            console.log("Emiting events for changes of event: ", lastChange.event.type, "from: ", from);
            if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.PLUGIN_MOVED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(lastChange.event.type, { newPosition: lastChange.event.payload.oldPosition, oldPosition: lastChange.event.payload.newPosition }, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.PLUGIN_DELETED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_CREATED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.PLUGIN_CREATED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_DELETED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONNECTOR_DELETED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_CREATED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONNECTOR_CREATED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_DELETED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.PLUGIN_LABEL_UPDATED) {
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_LABEL_UPDATED, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED) {
                lastChange.event.payload.forEach(action => this.emitEventsForAllChanges({ event: { type: action.event, payload: action.payload } }, from));
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_ADDED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_REMOVED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_REMOVED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_ADDED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_REMOVED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_ADDED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_ADDED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_REMOVED, lastChange.event.payload, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONTAINER_RESIZE) {

            } else if (lastChange.event.type === DIAGRAM_EMIT_EVENTS.CONTAINER_MOVED) {
                if (from === "UNDO") this.eventEmitter.emitEvent(lastChange.event.type, { newPosition: lastChange.event.payload.oldPosition, oldPosition: lastChange.event.payload.newPosition }, false, true);
                else this.eventEmitter.emitEvent(lastChange.event.type, lastChange.event.payload, false, true);
            }
        }
    }
    return FabricDiagramStateHolder;
}());
